package dev.lingvo.site.pages.app.settings.agents

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.enterStaggered
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.shared.SpendApprovalDto
import dev.lingvo.site.ds.LBulkActionBar
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LCheckCheckIcon
import dev.lingvo.site.ds.LCheckIcon
import dev.lingvo.site.ds.LCheckbox
import dev.lingvo.site.ds.LCircleXIcon
import dev.lingvo.site.ds.LCoinsIcon
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTaskProgress
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.requestWorkspaceAction
import kotlinx.coroutines.CancellationException
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.pages.app.settings.loadWorkspaceProtectedResource
import dev.lingvo.site.pages.app.settings.reloadWorkspaceProtectedResource
import dev.lingvo.site.pages.app.settings.rememberWorkspaceAuthorityState
import dev.lingvo.site.state.WorkspaceActionClaim
import dev.lingvo.site.state.WorkspacePrivilege
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.state.runWorkspaceAction
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

/**
 * Where focus goes when the bulk strip is cleared.
 *
 * This queue has no select-all header cell to hand focus back to, so the honest target is the
 * first card's own checkbox — the control the reader ticked to raise the strip in the first place.
 */
internal const val APPROVALS_FIRST_SELECT_ID = "approvals-first-select"

/**
 * What a refused decision says, once, for all three places that can refuse one.
 *
 * Deciding one request and deciding a selection of them are the same sentence to a reader, and the
 * bulk path was the only one of the three that looked it up: approving a single card said
 * "Couldn't decide this request. Retry?" in English while the bulk bar beside it said it in the
 * reader's language. `tr`, because every call site is an event handler.
 */
internal fun approvalDecisionRefused(): String = tr(
    "app.settings.agents.approvals.couldnt_decide_this_request_retry",
    "Couldn't decide this request. Retry?",
)

/**
 * Agents asking for money they do not have budget for.
 *
 * **The human at the MCP client is frequently not the human who pays.** An in-client prompt gets a
 * yes from somebody with no authority to give one, so the request lands here and an owner or admin
 * decides — usually after being sent this link by the person whose run is stuck.
 *
 * An approval is spent on one call. It is not a standing permission, and nothing here raises the
 * budget: the next call over the line asks again.
 */
@Page("/app/settings/agents/approvals")
@Layout(".components.layouts.AppLayout")
@Composable
fun SpendApprovalsPage() {
    val workspaceContext = WorkspaceRequestContext.current()
    val agentClaim = WorkspaceActionClaim.capture(workspaceContext, WorkspacePrivilege.AGENTS)

    var refreshKey by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf(0) }
    var busy by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf(false) }
    var message by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf<String?>(null) }
    var selected by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf(emptySet<String>()) }
    var threshold by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf(THRESHOLD_LADDER.first()) }
    var batch by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf<ApprovalBatch?>(null) }
    val scope = rememberCoroutineScope()

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(
            t("app.settings.agents.approvals.spend_requests", "Spend requests"),
            t("app.settings.agents.approvals.agents_that_hit_a_budget_and", "Agents that hit a budget and asked instead of failing."),
            mark = { LCoinsIcon() },
        )

        if (agentClaim == null) {
            LAlert(
                title = t("app.settings.agents.approvals.spend_requests_are_managed_by_an", "Spend requests are managed by an owner or admin"),
                description = t("app.settings.agents.approvals.your_role_in_this_workspace_cannot", "Your role in this workspace cannot view or decide agent spending."),
                variant = LAlertVariant.INFO,
            )
            return@Column
        }

        val reloadable = reloadWorkspaceProtectedResource(
            context = workspaceContext,
            privilege = WorkspacePrivilege.AGENTS,
            reloadToken = refreshKey,
            transport = ApiClient::spendApprovals,
        )

        // Never on screen at load — it exists only because an approve or a deny just failed.
        message?.let {
            LAlert(
                t("app.settings.agents.approvals.that_didnt_work", "That didn't work"),
                it,
                variant = LAlertVariant.WARNING,
                live = true,
            )
        }

        // A decided request that failed to reload stays on screen looking undecided. Saying so is
        // the difference between "the list is stale" and "your decision did not take".
        reloadable.refreshError?.let { failure ->
            LAlert(
                t("app.settings.agents.approvals.showing_the_last_list", "Showing the last list that loaded"),
                failure.readable(),
                modifier = Modifier.fillMaxWidth(),
                variant = LAlertVariant.WARNING,
                live = true,
            )
        }

        LAsyncContent(
            reloadable.state,
            rows = 2,
            loadingLabel = t("app.settings.agents.approvals.loading_requests", "Loading requests"),
            onRetry = { refreshKey++ },
        ) { response ->
            val approvals = response?.approvals.orEmpty()
            // Every decision this page can make goes through the same claim and the same one-shot
            // `decideSpend`, one request at a time, so a batch is exactly N single decisions with
            // one receipt — never a wider server call with weaker guarantees.
            val decideEach: (List<SpendApprovalDto>, Boolean) -> Unit = { targets, approve ->
                val actionClaim = agentClaim.takeIf { it.remainsAuthorized(WorkspaceRequestContext.current()) }
                if (actionClaim != null && targets.isNotEmpty()) {
                    batch = ApprovalBatch(total = targets.size, approving = approve)
                    scope.launch {
                        busy = true
                        targets.forEach { approval ->
                            val outcome = try {
                                requestWorkspaceAction(
                                    claim = actionClaim,
                                    currentContext = WorkspaceRequestContext::current,
                                ) { workspaceSlug ->
                                    ApiClient.decideSpend(workspaceSlug, approval.id, approve)
                                }
                                ApprovalOutcome(approval.agentName, approval.requestedCredits, null)
                            } catch (cancelled: CancellationException) {
                                throw cancelled
                            } catch (failure: Throwable) {
                                ApprovalOutcome(
                                    approval.agentName,
                                    approval.requestedCredits,
                                    failure.message?.takeIf(String::isNotBlank)
                                        ?: approvalDecisionRefused(),
                                )
                            }
                            batch = batch?.let { it.copy(results = it.results + outcome) }
                        }
                        selected = emptySet()
                        busy = false
                        message = null
                        refreshKey++
                    }
                }
            }

            batch?.let { run ->
                // The receipt: what the batch decided, per request, including what it could not.
                Column(Modifier.fillMaxWidth().gap(Space.XS)) {
                    LTaskProgress(
                        done = run.done,
                        total = run.total,
                        failed = run.failed,
                        label = if (run.approving) {
                            t("app.settings.agents.approvals.approving_the_selection", "Approving the selection")
                        } else {
                            t("app.settings.agents.approvals.denying_the_selection", "Denying the selection")
                        },
                    )
                    run.results.forEach { outcome ->
                        Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                            if (outcome.error == null) LCheckIcon() else LCircleXIcon()
                            Text(outcome.agentName)
                            LMuted(
                                outcome.error ?: tPlural(
                                    "app.settings.agents.approvals.count.credit_decided",
                                    outcome.credits,
                                    "{count} credit",
                                    "{count} credits",
                                ),
                            )
                        }
                    }
                    if (run.done >= run.total) {
                        LButton(
                            t("app.settings.agents.approvals.hide_this_receipt", "Hide this receipt"),
                            { batch = null },
                            variant = LButtonVariant.GHOST,
                            size = LButtonSize.SM,
                        )
                    }
                }
            }

            if (approvals.isNotEmpty()) {
                // **The threshold action, priced by the real list.** "Approve all" is the control
                // nobody should be offered; "approve the four requests asking for less than fifty
                // credits" is a decision somebody can actually make, and the total it will spend is
                // on the button before it is pressed.
                val underThreshold = approvals.filter { it.requestedCredits < threshold }
                Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.Bottom) {
                    LSelect(
                        threshold.toString(),
                        THRESHOLD_LADDER.map { rung ->
                            LSelectOption(
                                rung.toString(),
                                trf(
                                    "app.settings.agents.approvals.under_credits",
                                    "Under {count} credits",
                                    "count" to formatCount(rung),
                                ),
                            )
                        },
                        { value -> threshold = value.toIntOrNull() ?: THRESHOLD_LADDER.first() },
                        label = t("app.settings.agents.approvals.approve_up_to", "Approve up to"),
                    )
                    LButton(
                        tPlural(
                            "app.settings.agents.approvals.count.approve_request_under",
                            underThreshold.size,
                            "Approve {count} request",
                            "Approve {count} requests",
                        ),
                        { decideEach(underThreshold, true) },
                        variant = LButtonVariant.SECONDARY,
                        enabled = underThreshold.isNotEmpty() && !busy,
                        leadingIcon = { LCheckCheckIcon() },
                    )
                }
                LMuted(
                    tPlural(
                        "app.settings.agents.approvals.count.that_spends_credit_in_total",
                        underThreshold.sumOf { it.requestedCredits },
                        "That spends {count} credit in total, once.",
                        "That spends {count} credits in total, once.",
                    ),
                )
            }

            if (approvals.isEmpty()) {
                // The good state, and it should read as one rather than as an empty list.
                LEmptyState(
                    t("app.settings.agents.approvals.nothing_is_waiting", "Nothing is waiting"),
                    t("app.settings.agents.approvals.requests_appear_here_when_an_agent_2", "Requests appear here when an agent tries to spend past its daily or monthly " +
                        "budget. A call over the per-call ceiling is refused outright and never " +
                        "reaches this page."),
                    Modifier.fillMaxWidth(),
                    LEmptyStateIcon.SUCCESS,
                ) {}
            } else {
                approvals.forEachIndexed { index, approval ->
                    // A queue of pending approvals is a list being assembled, which is the case
                    // `enterStaggered` exists for: the same content arriving in sequence reads as a
                    // page putting itself together rather than one that was slow.
                    LCard(Modifier.fillMaxWidth().enterStaggered(index)) {
                        Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                            Row(
                                Modifier.fillMaxWidth().gap(Space.SM),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                LCheckbox(
                                    checked = approval.id in selected,
                                    onCheckedChange = { on ->
                                        selected = if (on) selected + approval.id else selected - approval.id
                                    },
                                    modifier = if (index == 0) {
                                        Modifier.attr("id", APPROVALS_FIRST_SELECT_ID)
                                    } else {
                                        Modifier
                                    },
                                    // Names this request rather than the column, so the fourth
                                    // checkbox in a list is distinguishable from the third.
                                    label = trf(
                                        "app.settings.agents.approvals.select_the_request_from_agent",
                                        "Select the request from {agent}",
                                        "agent" to approval.agentName,
                                    ),
                                    labelHidden = true,
                                    enabled = !busy,
                                )
                                LSectionTitle(approval.agentName, Modifier.flexGrow(1))
                                approval.projectSlug?.let { LBadge(it) }
                            }
                            P(Modifier.tabularNumbers().margin(0.px).toAttrs()) {
                                Text(tPlural(
                                    "app.settings.agents.approvals.requested_credits",
                                    approval.requestedCredits,
                                    "{count} credit",
                                    "{count} credits",
                                ))
                            }
                            // Frozen at the moment of asking. By the time somebody looks the live
                            // number has usually moved, and this is the fact that made the
                            // request reasonable or not.
                            LMuted(
                                tf(
                                    "app.settings.agents.approvals.budget_left_when_asked",
                                    "It had {remaining} left when it asked, at {when} UTC.",
                                    "remaining" to approval.remainingBudget,
                                    "when" to approval.createdAt.take(16).replace('T', ' '),
                                ),
                            )
                            approval.reason?.let { reason ->
                                // The agent's own words. Rendered as text, never markup: this is
                                // the one field on the page an agent controls.
                                LMuted(tf(
                                    "app.settings.agents.approvals.it_says",
                                    "It says: {reason}",
                                    "reason" to reason,
                                ))
                            }
                            LMuted(tf(
                                "app.settings.agents.approvals.expires_if_undecided",
                                "Expires {when} UTC if nobody decides.",
                                "when" to approval.expiresAt.take(16).replace('T', ' '),
                            ))
                            Row(Modifier.gap(Space.SM)) {
                                SpendApprovalAction(
                                    pageClaim = agentClaim,
                                    label = t("app.settings.agents.approvals.approve_once", "Approve once"),
                                    approvalId = approval.id,
                                    approve = true,
                                    busy = busy,
                                    setBusy = { busy = it },
                                    onDone = {
                                        refreshKey++
                                        message = null
                                    },
                                    onFailure = { error ->
                                        message = error.message?.takeIf(String::isNotBlank)
                                            ?: approvalDecisionRefused()
                                    },
                                )
                                SpendApprovalAction(
                                    pageClaim = agentClaim,
                                    label = t("app.settings.agents.approvals.deny", "Deny"),
                                    approvalId = approval.id,
                                    approve = false,
                                    busy = busy,
                                    setBusy = { busy = it },
                                    variant = LButtonVariant.GHOST,
                                    onDone = {
                                        refreshKey++
                                        message = null
                                    },
                                    onFailure = { error ->
                                        message = error.message?.takeIf(String::isNotBlank)
                                            ?: approvalDecisionRefused()
                                    },
                                )
                            }
                            LMuted(t("app.settings.agents.approvals.approving_covers_this_one_spend_it", "Approving covers this one spend. It does not raise the budget."))
                        }
                    }
                }
                val chosen = approvals.filter { it.id in selected }
                LBulkActionBar(
                    selectedCount = chosen.size,
                    onClear = { selected = emptySet() },
                    label = tPlural(
                        "app.settings.agents.approvals.count.request_selected",
                        chosen.size,
                        "{count} request selected",
                        "{count} requests selected",
                    ),
                    // The money, on the strip that is about to spend it.
                    note = tPlural(
                        "app.settings.agents.approvals.count.credit_if_you_approve",
                        chosen.sumOf { it.requestedCredits },
                        "{count} credit if you approve them.",
                        "{count} credits if you approve them.",
                    ),
                    returnFocusToId = APPROVALS_FIRST_SELECT_ID,
                ) {
                    LButton(
                        t("app.settings.agents.approvals.approve_selected", "Approve selected"),
                        { decideEach(chosen, true) },
                        size = LButtonSize.SM,
                        enabled = !busy,
                        leadingIcon = { LCheckCheckIcon() },
                    )
                    LButton(
                        t("app.settings.agents.approvals.deny_selected", "Deny selected"),
                        { decideEach(chosen, false) },
                        size = LButtonSize.SM,
                        variant = LButtonVariant.GHOST,
                        enabled = !busy,
                        leadingIcon = { LCircleXIcon() },
                    )
                }
            }
        }
    }
}

/**
 * The rungs the threshold action offers.
 *
 * A ladder rather than a free number field: the control is "approve the small ones without reading
 * each", and a typed threshold invites a digit slipping in. Every rung is a round number somebody
 * can reason about against a monthly budget.
 */
private val THRESHOLD_LADDER = listOf(10, 50, 100, 500)

/** One batch of spend decisions: how far it got, and what each request answered. */
internal data class ApprovalBatch(
    val total: Int,
    val approving: Boolean,
    val results: List<ApprovalOutcome> = emptyList(),
) {
    val done: Int get() = results.size
    val failed: Int get() = results.count { it.error != null }
}

internal data class ApprovalOutcome(val agentName: String, val credits: Int, val error: String?)

@Composable
internal fun SpendApprovalAction(
    pageClaim: WorkspaceActionClaim,
    label: String,
    approvalId: String,
    approve: Boolean,
    busy: Boolean,
    setBusy: (Boolean) -> Unit,
    variant: LButtonVariant = LButtonVariant.PRIMARY,
    currentContext: () -> WorkspaceRequestContext? = WorkspaceRequestContext::current,
    decide: suspend (workspaceSlug: String, approvalId: String, approve: Boolean) -> Unit =
        ApiClient::decideSpend,
    launchAction: ((suspend () -> Unit) -> Unit)? = null,
    onDone: () -> Unit,
    onFailure: (Throwable) -> Unit,
) {
    val scope = rememberCoroutineScope()
    val launch: (suspend () -> Unit) -> Unit = launchAction ?: { action ->
        scope.launch { action() }
    }
    LButton(
        label,
        {
            val actionClaim = pageClaim
                .takeIf { it.remainsAuthorized(currentContext()) }
                ?: return@LButton
            launch {
                runWorkspaceAction(
                    claim = actionClaim,
                    currentContext = currentContext,
                    setBusy = setBusy,
                    transport = { workspaceSlug -> decide(workspaceSlug, approvalId, approve) },
                    onSuccess = { onDone() },
                    onFailure = onFailure,
                )
            }
        },
        variant = variant,
        enabled = !busy,
    )
}

